import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'wagmi';
import { QRCodeSVG } from 'qrcode.react';
import { isAddress } from 'viem';
import { toast } from 'sonner';
import { useUserPortfolio } from '../../hooks/contract/useUserPortfolio';
import { useClaimReferral, useUpdateSponsor } from '../../hooks/contract/useWriteActions';
import {
  REWARD_TIERS,
  SPONSOR_GRACE_PERIOD_SEC,
} from '../../lib/constants';
import { formatUsdt, shortAddr, bscAddr, formatDuration } from '../../lib/format';

const TIER_CLASS = (n) => {
  if (n === 1) return 'diamond';
  if (n === 2) return 'platinum';
  if (n <= 7) return 'gold';
  if (n <= 10) return 'silver';
  return 'bronze';
};

const Referrals = () => {
  const { t } = useTranslation();
  const { address } = useAccount();
  const { userInfo, refetch } = useUserPortfolio();
  const claimRef = useClaimReferral();
  const updateSponsor = useUpdateSponsor();

  const [newSponsor, setNewSponsor] = useState('');
  const [showQr, setShowQr] = useState(false);

  const link = useMemo(() => {
    if (!address) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/?ref=${address}`;
  }, [address]);

  const pendingReferral = userInfo?.pendingReferral ?? 0n;

  const now = Math.floor(Date.now() / 1000);
  const firstDepositAt = userInfo?.firstDepositAt ? Number(userInfo.firstDepositAt) : 0;
  const graceEndsAt = firstDepositAt + SPONSOR_GRACE_PERIOD_SEC;
  const inGrace = firstDepositAt > 0 && now < graceEndsAt;
  const graceRemaining = Math.max(0, graceEndsAt - now);

  const hasSponsor =
    userInfo?.sponsor &&
    userInfo.sponsor !== '0x0000000000000000000000000000000000000000';

  const copy = (value) => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(t('dashboard.referrals.copied')))
      .catch(() => toast.error(t('dashboard.referrals.copyFailed')));
  };

  const shareLinks = useMemo(() => {
    if (!link) return [];
    const text = t('dashboard.referrals.shareText');
    return [
      {
        id: 'telegram',
        icon: 'fa-brands fa-telegram',
        href: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`,
      },
      {
        id: 'twitter',
        icon: 'fa-brands fa-x-twitter',
        href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`,
      },
      {
        id: 'whatsapp',
        icon: 'fa-brands fa-whatsapp',
        href: `https://wa.me/?text=${encodeURIComponent(text + ' ' + link)}`,
      },
    ];
  }, [link, t]);

  return (
    <div className="db-page db-page--referrals">
      <div className="db-page__head">
        <h1 className="db-page__title">{t('dashboard.referrals.title')}</h1>
        <p className="db-page__subtitle">{t('dashboard.referrals.subtitle')}</p>
      </div>

      {/* Link block */}
      <div className="ref-link-card">
        <div className="ref-link-card__head">
          <h3>{t('dashboard.referrals.yourLink')}</h3>
          <button
            type="button"
            className="sf-btn sf-btn--outline sf-btn--sm"
            onClick={() => setShowQr((v) => !v)}
          >
            <i className="fas fa-qrcode" /> {t('dashboard.referrals.qr')}
          </button>
        </div>
        <div className="ref-link-card__row">
          <input readOnly className="ref-link-card__input mono" value={link} />
          <button
            type="button"
            className="sf-btn sf-btn--gold sf-btn--sm"
            onClick={() => copy(link)}
          >
            <i className="fas fa-copy" /> {t('dashboard.referrals.copy')}
          </button>
        </div>
        {showQr && link && (
          <div className="ref-link-card__qr">
            <QRCodeSVG value={link} size={160} bgColor="#0A0A0B" fgColor="#FFCB47" level="M" />
          </div>
        )}
        <div className="ref-link-card__share">
          <span>{t('dashboard.referrals.shareVia')}</span>
          {shareLinks.map((s) => (
            <a
              key={s.id}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="ref-link-card__share-btn"
              aria-label={s.id}
            >
              <i className={s.icon} />
            </a>
          ))}
        </div>
      </div>

      {/* Claim card */}
      <div className="ref-claim-card">
        <div className="ref-claim-card__main">
          <div className="ref-claim-card__label">{t('dashboard.referrals.pendingLabel')}</div>
          <div className="ref-claim-card__value mono tabular-nums">
            {formatUsdt(pendingReferral)} USDT
          </div>
          <div className="ref-claim-card__hint">{t('dashboard.referrals.pendingHint')}</div>
        </div>
        <button
          type="button"
          className="sf-btn sf-btn--gold"
          onClick={() => claimRef.claim()}
          disabled={
            pendingReferral === 0n ||
            claimRef.status === 'signing' ||
            claimRef.status === 'confirming'
          }
        >
          <i className="fas fa-sack-dollar" /> {t('dashboard.referrals.claim')}
        </button>
      </div>

      {/* Sponsor info */}
      <div className="ref-sponsor-card">
        <div className="ref-sponsor-card__label">{t('dashboard.referrals.yourSponsor')}</div>
        {hasSponsor ? (
          <a
            className="ref-sponsor-card__addr mono"
            href={bscAddr(userInfo.sponsor)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shortAddr(userInfo.sponsor)} <i className="fas fa-arrow-up-right-from-square" />
          </a>
        ) : (
          <div className="ref-sponsor-card__none">{t('dashboard.referrals.noSponsor')}</div>
        )}

        {inGrace && (
          <div className="ref-sponsor-card__grace">
            <i className="fas fa-shield-halved" />
            <div>
              <strong>
                {t('dashboard.referrals.graceTitle', { time: formatDuration(graceRemaining) })}
              </strong>
              <div>{t('dashboard.referrals.graceDesc')}</div>
            </div>
            <div className="ref-sponsor-card__grace-form">
              <input
                type="text"
                className="mono"
                placeholder="0x…"
                value={newSponsor}
                onChange={(e) => setNewSponsor(e.target.value)}
              />
              <button
                type="button"
                className="sf-btn sf-btn--outline sf-btn--sm"
                onClick={async () => {
                  if (!isAddress(newSponsor)) {
                    toast.error(t('dashboard.new.step3.invalidAddr'));
                    return;
                  }
                  const res = await updateSponsor.update(newSponsor);
                  if (res.success) {
                    setNewSponsor('');
                    refetch?.();
                  }
                }}
                disabled={updateSponsor.status === 'signing' || updateSponsor.status === 'confirming'}
              >
                {t('dashboard.referrals.updateSponsor')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Generation breakdown (структура программы) */}
      <div className="db-block">
        <div className="db-block__head">
          <h3>{t('dashboard.referrals.structureTitle')}</h3>
          <span className="db-block__note">{t('dashboard.referrals.structureNote')}</span>
        </div>
        <div className="gen-table">
          <div className="gen-table__head">
            <span>{t('dashboard.referrals.genCol.gen')}</span>
            <span>{t('dashboard.referrals.genCol.pct')}</span>
            <span>{t('dashboard.referrals.genCol.tier')}</span>
          </div>
          {REWARD_TIERS.map((g) => (
            <div key={g.tier} className={`gen-table__row gen-table__row--${TIER_CLASS(g.tier)}`}>
              <span className="mono">{t('dashboard.referrals.tier', { n: g.tier })}</span>
              <span className="mono gold-text">{g.label}</span>
              <span className="gen-table__tier">
                {t(`dashboard.referrals.tierClass.${TIER_CLASS(g.tier)}`)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="db-callout">
        <i className="fas fa-circle-info" />
        <div>
          <strong>{t('dashboard.referrals.v2Title')}</strong>
          <div>{t('dashboard.referrals.v2Desc')}</div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
